import { useCallback, useEffect, useRef, useState } from 'react';

/** How far from the bottom still counts as pinned, in px. */
const PINNED_SLACK = 32;

const isPinned = (node: HTMLElement): boolean =>
    node.scrollHeight - node.scrollTop - node.clientHeight <= PINNED_SLACK;

/**
 * Keeps a scroll container pinned to its newest content while the reader is at the bottom,
 * and reports when they have scrolled away.
 *
 * `contentRef` goes on the growing child rather than the container: a streaming reply changes
 * the content's height, never the container's, so an observer on the container never fires.
 */
export const useStickToBottom = () => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [pinned, setPinned] = useState(true);

    // Whether the reader was pinned BEFORE the content grew. By the time the observer runs,
    // the container is no longer at the bottom, so the live DOM cannot answer this.
    const pinnedRef = useRef(true);

    const scrollToBottom = useCallback(() => {
        const node = scrollRef.current;

        if (!node) {
            return;
        }

        // Eased, because arriving instantly at the bottom reads as a glitch rather than as
        // travel. `scrollTo` is asked for smooth only when the reader has not opted out of
        // motion — matchMedia rather than CSS, since this is a scroll and not a style.
        const smooth =
            typeof window.matchMedia === 'function' &&
            !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (smooth && typeof node.scrollTo === 'function') {
            node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' });
        } else {
            node.scrollTop = node.scrollHeight;
        }

        // Pinned because this just pinned it, rather than waiting to be told: Chrome can
        // coalesce the scroll event a programmatic scroll produces, which would leave the
        // jump-to-latest affordance on screen at the latest message.
        pinnedRef.current = true;
        setPinned(true);
    }, []);

    useEffect(() => {
        const node = scrollRef.current;
        const content = contentRef.current;

        if (!node || !content) {
            return;
        }

        const readPinned = () => {
            const next = isPinned(node);
            pinnedRef.current = next;
            setPinned(next);
        };

        node.addEventListener('scroll', readPinned, { passive: true });

        // jsdom has no ResizeObserver, and a thread that cannot follow its content is still
        // a working transcript — so pinning degrades rather than throwing on mount.
        const observer =
            typeof ResizeObserver === 'undefined'
                ? undefined
                : new ResizeObserver(() => {
                      if (pinnedRef.current) {
                          node.scrollTop = node.scrollHeight;
                      }
                  });
        observer?.observe(content);

        return () => {
            node.removeEventListener('scroll', readPinned);
            observer?.disconnect();
        };
    }, []);

    return { scrollRef, contentRef, pinned, scrollToBottom };
};
